/**
 * 從瀏覽器直連 TWSE mis API（撮合系統 ~5-10 秒延遲）
 * 繞過 Azure Function（datacenter 連 TWSE 常被擋）
 * mis.twse.com.tw 對 SPA 開放 CORS
 */

interface TwseMisItem {
  c?: string;
  n?: string;
  z?: string;      // 最新成交
  o?: string;      // 開盤
  h?: string;      // 高
  l?: string;      // 低
  y?: string;      // 昨收
  v?: string;      // 累計量
  tlong?: string;
}

export interface TwseRealtimeQuote {
  symbol: string;
  name?: string;
  price: number;
  prevClose: number;
  change: number;
  changePct: number;
  open?: number;
  high?: number;
  low?: number;
  volume?: number;
  ts?: number;
}

/**
 * @param yahooSymbol e.g. '2330.TW' or '6488.TWO'
 * @returns TWSE 資料；失敗時 throw (這樣 react-query 會保留上一次成功資料，避免 UI 閃爍)
 */
export async function fetchTwseRealtimeFromBrowser(
  yahooSymbol: string,
  signal?: AbortSignal,
): Promise<TwseRealtimeQuote | null> {
  const m = yahooSymbol.match(/^(\d{4,6})\.(TW|TWO)$/i);
  if (!m) return null; // 非台股：query enabled=false 不會到這
  const code = m[1]!;
  const market = m[2]!.toUpperCase();
  const prefix = market === 'TWO' ? 'otc' : 'tse';
  const ex_ch = `${prefix}_${code}.tw`;

  const url = `https://mis.twse.com.tw/stock/api/getStockInfo.jsp?ex_ch=${encodeURIComponent(ex_ch)}&json=1&delay=0&_=${Date.now()}`;

  const res = await fetch(url, { cache: 'no-store', signal });
  if (!res.ok) throw new Error(`TWSE HTTP ${res.status}`);
  const data = (await res.json()) as { msgArray?: TwseMisItem[] };
  const item = data.msgArray?.[0];
  if (!item) throw new Error('TWSE empty');

  // z 可能是 '-' (撮合空檔)，用 (h+l)/2 估代理價
  let priceStr = item.z;
  if (!priceStr || priceStr === '-') {
    const h = parseNum(item.h);
    const l = parseNum(item.l);
    if (h != null && l != null) {
      priceStr = ((h + l) / 2).toFixed(2);
    } else {
      throw new Error('TWSE z=-, no h/l');
    }
  }

  const price = parseFloat(priceStr);
  const prevClose = parseFloat(item.y ?? '0');
  if (!Number.isFinite(price) || !Number.isFinite(prevClose) || prevClose === 0) {
    throw new Error('TWSE invalid price');
  }

  const change = price - prevClose;
  const changePct = (change / prevClose) * 100;

  return {
    symbol: yahooSymbol,
    name: item.n,
    price,
    prevClose,
    change,
    changePct,
    open: parseNum(item.o),
    high: parseNum(item.h),
    low: parseNum(item.l),
    volume: parseNum(item.v),
    ts: item.tlong ? Number(item.tlong) : Date.now(),
  };
}

function parseNum(s?: string): number | undefined {
  if (!s || s === '-') return undefined;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : undefined;
}
